import { Check, HelpCircle, X } from 'lucide-react';
import React, { useCallback } from 'react';

import { cn } from '@/lib/utils';

import { ScreeningAnswer } from './types';

interface ScreeningToggleProps {
  value: ScreeningAnswer;
  onChange: (value: ScreeningAnswer) => void;
  disabled?: boolean;
  /** If true, only show Yes/No (no Unknown option) */
  binaryOnly?: boolean;
  /** If true, use smaller toggle for sub-questions */
  compact?: boolean;
  /** If true, show pulsing border to indicate this needs user attention */
  needsAttention?: boolean;
  className?: string;
}

export const ScreeningToggle: React.FC<ScreeningToggleProps> = ({
  value,
  onChange,
  disabled = false,
  binaryOnly = false,
  compact = false,
  needsAttention = false,
  className,
}) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      const key = e.key.toLowerCase();
      if (key === 'y') {
        e.preventDefault();
        onChange('yes');
      } else if (key === 'n') {
        e.preventDefault();
        onChange('no');
      } else if (key === 'u' && !binaryOnly) {
        e.preventDefault();
        onChange('unknown');
      }
    },
    [disabled, binaryOnly, onChange],
  );

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-md border border-zinc-200 bg-white',
        disabled && 'opacity-50',
        needsAttention && 'border-zinc-400',
        className,
      )}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="radiogroup"
      aria-label="Answer selection"
    >
      {/* Yes button */}
      <button
        type="button"
        onClick={() => !disabled && onChange('yes')}
        disabled={disabled}
        className={cn(
          'flex items-center gap-0.5 font-medium transition-colors',
          compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-1.5 text-sm gap-1',
          'rounded-l-md border-r border-zinc-200',
          'focus:outline-none focus:ring-1 focus:ring-indigo-500',
          value === 'yes'
            ? 'bg-emerald-500 text-white'
            : 'bg-white text-zinc-600 hover:bg-zinc-50',
        )}
        role="radio"
        aria-checked={value === 'yes'}
        aria-label="Yes"
      >
        <Check className={compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} />
        {!compact && <span>Yes</span>}
      </button>

      {/* No button */}
      <button
        type="button"
        onClick={() => !disabled && onChange('no')}
        disabled={disabled}
        className={cn(
          'flex items-center gap-0.5 font-medium transition-colors',
          compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-1.5 text-sm gap-1',
          binaryOnly ? 'rounded-r-md' : 'border-r border-zinc-200',
          'focus:outline-none focus:ring-1 focus:ring-indigo-500',
          value === 'no'
            ? 'bg-rose-500 text-white'
            : 'bg-white text-zinc-600 hover:bg-zinc-50',
        )}
        role="radio"
        aria-checked={value === 'no'}
        aria-label="No"
      >
        <X className={compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} />
        {!compact && <span>No</span>}
      </button>

      {/* Unknown button */}
      {!binaryOnly && (
        <button
          type="button"
          onClick={() => !disabled && onChange('unknown')}
          disabled={disabled}
          className={cn(
            'flex items-center gap-0.5 font-medium transition-colors',
            compact
              ? 'px-1.5 py-0.5 text-[10px]'
              : 'px-2.5 py-1.5 text-sm gap-1',
            'rounded-r-md',
            'focus:outline-none focus:ring-1 focus:ring-indigo-500',
            value === 'unknown'
              ? 'bg-zinc-700 text-white'
              : 'bg-white text-zinc-600 hover:bg-zinc-50',
          )}
          role="radio"
          aria-checked={value === 'unknown'}
          aria-label="Unknown"
        >
          <HelpCircle className={compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} />
          {!compact && <span>Unk</span>}
        </button>
      )}
    </div>
  );
};
